import React, { useState } from 'react';
import { useAuth } from 'lib/context';
import styled, { useTheme } from 'styled-components/macro';
import { useIsMutating } from 'react-query';
import { getIn, useFormikContext } from 'formik';
import { Button } from 'react-covideo-common';
import { Separator } from '../preview/styles';
import { FaChevronLeft } from 'react-icons/fa';
import { Flex, Gap } from 'lib/components/styles/layout';
import { FontSettings } from './shared/FontSettings';
import { ColorSettings } from './shared/ColorSettings';
import { SaveUIKitModal } from '../uikit/modals/SaveUIKitModal';
import { SaveUIKitAction } from '../uikit/UIKit';
import { useCreateUIKitMutation } from 'lib/api/superadmin/uikits/useCreateUIKitMutation';
import { useUpdateUIKitMutation } from 'lib/api/superadmin/uikits/useUpdateUIKitMutation';
import { ILandingPageBuilderForm } from '../../types';
import { FormikImageUpload } from 'lib/components/formik/FormikImageUpload/FormikImageUpload';
import { uploadImage } from 'lib/api';
import { buildImageField } from '../modal/LandingPageBuilderModal';
import { FormikDropdownOption } from 'lib/components/formik/FormikDropdown';

interface UIKitFormProps {
  selectedUIKitIndex: number;
  saveAction: SaveUIKitAction | null;
  toggleShowSaveUIKitModal: (action: SaveUIKitAction | null) => void;
  handleGoBack: () => void;
  allFontFamilyOptions: FormikDropdownOption[];
  unshift: <X = any>(value: X) => number;
  replace: <X = any>(index: number, value: X) => void;
  remove: <X = any>(index: number) => X | undefined;
  push: <X = any>(obj: X) => void;
}

const UIKitFormTitle = styled.h1`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  color: ${props => props.theme.colors.neutral[100]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 90%;
`;

export const UIKitUpsertForm = ({
  selectedUIKitIndex,
  saveAction,
  toggleShowSaveUIKitModal,
  handleGoBack,
  allFontFamilyOptions,
  unshift,
  replace,
  remove,
  push,
}: UIKitFormProps) => {
  const theme = useTheme();
  const { userData } = useAuth();
  const userId = userData?.id;
  const { values, errors } = useFormikContext<ILandingPageBuilderForm>();
  const name = `uiKits[${selectedUIKitIndex}].thumbnail`;
  const thumbnailError: { url: string } = getIn(errors, name);

  const selectedUIKit = values.uiKits[selectedUIKitIndex];
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);

  const isMutating = useIsMutating();
  const isLoading = isMutating > 0 || isUploadingThumbnail;

  const isUpdate = values.uiKits[selectedUIKitIndex]?.id;
  const saveAs = saveAction === SaveUIKitAction.SAVE_AS;
  const updateButtonText = `${isUpdate ? 'Update' : 'Save'} UI Kit`;

  const { mutateAsync: create } = useCreateUIKitMutation(handleGoBack);
  const { mutateAsync: update } = useUpdateUIKitMutation(handleGoBack);

  const formTitle = isUpdate
    ? values.uiKits[selectedUIKitIndex]?.title
    : 'Add New UI Kit';

  const saveUIKitHandler = async () => {
    const currentIdx =
      isUpdate && !saveAs ? selectedUIKitIndex : values.uiKits.length - 1;
    const selectedUIKit = values.uiKits[currentIdx];
    const { id, thumbnail, title, ...restUIKit } = selectedUIKit;

    let thumbnailUrl = thumbnail?.url;
    if (thumbnail?.file) {
      setIsUploadingThumbnail(true);
      thumbnailUrl = await uploadImage(thumbnail.file, userId);
    }

    const payload = {
      ...restUIKit,
      title: title.trim(),
      thumbnail: thumbnailUrl,
    };

    const data =
      isUpdate && !saveAs
        ? await update({ id, payload })
        : await create(payload);

    if (data) {
      const newUIKit = {
        ...data.data,
        thumbnail: buildImageField(null, data.data.thumbnail),
      };

      if (isUpdate) {
        if (saveAs) {
          remove(values.uiKits.length - 1);
          unshift(newUIKit);
        } else {
          replace(currentIdx, newUIKit);
        }
      } else {
        remove(values.uiKits.length - 1);
        unshift(newUIKit);
      }
    }
    setIsUploadingThumbnail(false);
  };

  return (
    <Flex gap='24px'>
      <Flex flexDirection='row' gap='9px' alignItems='center'>
        <FaChevronLeft
          width={6}
          height={11}
          style={{ cursor: 'pointer' }}
          onClick={handleGoBack}
        />
        <UIKitFormTitle title={formTitle}>{formTitle}</UIKitFormTitle>
      </Flex>
      <Separator />
      {selectedUIKit && (
        <Flex key={selectedUIKitIndex} gap='16px'>
          <FormikImageUpload
            name={`uiKits[${selectedUIKitIndex}].thumbnail`}
            text='Upload Logo'
            isRequired={true}
            disabled={false}
            label={'Thumbnail'}
            targetWidth={500}
            extendStyles={{
              asteriskStyles: {
                color: theme.colors.neutral[80],
              },
              labelStyles: {
                fontSize: '12px',
                color: theme.colors.neutral[80],
                fontWeight: 600,
              },
              containerStyles: { height: '100px' },
              placeholderStyles: { fontSize: '14px' },
            }}
          />
          <Flex gap='24px'>
            <ColorSettings index={selectedUIKitIndex} />
            <FontSettings
              index={selectedUIKitIndex}
              allFontFamilyOptions={allFontFamilyOptions}
            />
          </Flex>
        </Flex>
      )}
      <Separator />
      <Gap alignItems='center' justifyContent='center' flexWrap='wrap'>
        <Button
          text='Cancel'
          size='small'
          variant='secondary'
          onClick={handleGoBack}
          style={{ width: '132px' }}
          disabled={isLoading}
        />
        <Button
          text={updateButtonText}
          size='small'
          onClick={
            isUpdate
              ? saveUIKitHandler
              : () => toggleShowSaveUIKitModal(SaveUIKitAction.UPSERT)
          }
          style={{ width: '132px' }}
          loading={isLoading}
          disabled={isLoading || !!thumbnailError?.url}
        />
        {isUpdate && (
          <Button
            text='Save as New UI Kit'
            size='small'
            onClick={() => {
              const {
                id: _id,
                createdAt: _createdAt,
                updatedAt: _updatedAt,
                ...rest
              } = values.uiKits[selectedUIKitIndex];
              push({ ...rest, title: '' });
              toggleShowSaveUIKitModal(SaveUIKitAction.SAVE_AS);
            }}
            style={{ width: '100%' }}
            loading={isLoading}
            disabled={isLoading || !!thumbnailError?.url}
          />
        )}
      </Gap>
      {saveAction && (
        <SaveUIKitModal
          closeModalHandler={() => {
            remove(values.uiKits.length - 1);
            toggleShowSaveUIKitModal(null);
          }}
          onSubmitHandler={saveUIKitHandler}
          selectedUIKitIndex={values.uiKits.length - 1}
          isLoading={isLoading}
        />
      )}
    </Flex>
  );
};
